import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuill } from 'react-quilljs'
import { useParams } from 'react-router-dom'
import { Modal, Skeleton } from 'antd'
import { Building } from 'models/Building'
import useSWR from 'swr'

import Spinner from 'components/atoms/Spinner'
import CloseIcon from 'components/icons/CloseIcon'

export default function BusinessInfoModal({
  setOpen
}: {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { t } = useTranslation()
  const { id: buildingId } = useParams()
  const { data: buildingInfo, isLoading } = useSWR<Building>({
    url: `v1/buildings/${buildingId}`
  })
  const [isReadingDescription, setIsReadingDescription] = useState(false)
  const { quill, quillRef } = useQuill({
    modules: { toolbar: false },
    readOnly: true
  }) // Get the Quill instance

  useEffect(() => {
    const loadInitialData = async () => {
      quill && quill.clipboard.dangerouslyPasteHTML('')
      if (buildingInfo?.extraInfo.descriptionUrl) {
        setIsReadingDescription(true)
        try {
          if (buildingInfo?.extraInfo?.descriptionUrl !== '') {
            const response = await fetch(buildingInfo?.extraInfo.descriptionUrl)
            const text = await response.text()
            if (quill) {
              quill.clipboard.dangerouslyPasteHTML(text) // Use Quill's pasteHTML
            }
          }
          setIsReadingDescription(false)
        } catch (error) {
          setIsReadingDescription(false)
        }
      } else {
        quill && quill.clipboard.dangerouslyPasteHTML('') // Use Quill's pasteHTML
      }
    }

    loadInitialData()
  }, [buildingInfo, quill]) // Depend on the Quill instance

  return (
    <Modal
      open
      onCancel={() => setOpen(false)}
      footer={null}
      closeIcon={<CloseIcon />}
      className="business-modal"
      width={window.innerWidth * 0.8}
      destroyOnClose
      centered
    >
      <Spinner spinning={isLoading || isReadingDescription}>
        {buildingInfo?.extraInfo?.descriptionUrl === '' &&
          buildingInfo.extraInfo.businessImageUrl === '' && (
            <div className="flex justify-center items-center w-full h-[275px]">
              {t('building.noBusinessInfo')}
            </div>
          )}
        {(buildingInfo?.extraInfo.descriptionUrl ||
          buildingInfo?.extraInfo.businessImageUrl) && (
          <div className="flex flex-col h-full">
            <div className="flex items-center p-[23px] h-[50%]">
              {buildingInfo?.extraInfo?.businessImageUrl && (
                <div className="w-full h-full flex justify-center items-center">
                  {isLoading && (
                    <Skeleton.Image
                      active
                      style={{ width: 100, height: 200 }}
                    />
                  )}
                  <img
                    className={`object-fill max-h-full w-full ${
                      isLoading ? 'hidden' : ''
                    }`}
                    src={buildingInfo?.extraInfo?.businessImageUrl}
                    alt="businessLogoImage"
                    loading="lazy"
                  />
                </div>
              )}
            </div>
            <div
              className={`${
                buildingInfo?.extraInfo?.businessImageUrl
                  ? 'h-[250px] '
                  : 'h-full max-h-[450px] pb-2'
              } w-full overflow-y-auto`}
            >
              <div
                ref={quillRef}
                className="quill-custom-editor overflow-hidden [&>#editor-resizer>.toolbar]:!hidden [&>.ql-editor]:pb-10"
                style={{ border: 'none' }}
              />
            </div>
          </div>
        )}
      </Spinner>
    </Modal>
  )
}
